import { Component, Element, Listen, Method, Prop, State, Watch } from '@stencil/core';

/**
 * Basic unstyled accessible accordion component. To be used with `lg-accordion-item` components.
 *
 * @exampleComponent lg-accordion-example
 * @exampleComponent lg-accordion-styled-example
 */
@Component({
  tag: 'lg-accordion',
  styleUrl: 'lg-accordion.scss',
})
export class LgAccordion {
  @Element() el: HTMLLgAccordionElement;

  mo: MutationObserver;

  /**
   * Whether only one accordion item can be open at a time.
   */
  @Prop() onlySingleOpen = false;

  @Watch('onlySingleOpen')
  onlySingleOpenChanged(): void {
    this.ensureOnlyOneOpen();
  }

  @Listen('lgAccordionToggle')
  onItemToggle(e: CustomEvent<{ open: boolean }>): void {
    if (this.onlySingleOpen && e.detail.open) {
      this.closeOthers(e.target as HTMLLgAccordionItemElement);
    }
  }

  @State() elItems: HTMLLgAccordionItemElement[] = [];

  @Watch('elItems')
  elItemsChanged(): void {
    this.ensureOnlyOneOpen();
  }

  private async toggleItems(open: boolean, index?: number): Promise<void> {
    if (index === undefined) {
      this.elItems.forEach(el => el.toggle(open));
    } else {
      this.elItems[index].toggle(open);
    }
  }

  /**
   * Open one or all accordion items.
   *
   * @param index The index of the accordion item to open. If not provided, all items will be opened.
   */
  @Method()
  async open(index?: number): Promise<void> {
    this.toggleItems(true, index);
  }

  /**
   * Close one or all accordion items.
   *
   * @param index The index of the accordion item to close. If not provided, all items will be closed.
   */
  @Method()
  async close(index?: number): Promise<void> {
    this.toggleItems(false, index);
  }

  connectedCallback() {
    this.collectItems();

    // update on items change
    this.mo = new MutationObserver(() => {
      this.collectItems();
    });

    this.mo.observe(this.el, {
      childList: true,
    });
  }

  disconnectedCallback() {
    this.mo.disconnect();
  }

  collectItems(): void {
    this.elItems = Array.from(this.el.children) as HTMLLgAccordionItemElement[];
  }

  ensureOnlyOneOpen(): void {
    if (!this.onlySingleOpen) {
      return;
    }

    const elOpenItems = this.elItems.filter(el => el.open);

    if (elOpenItems.length > 1) {
      // close all but the first open item
      this.closeOthers(elOpenItems[0]);
    }
  }

  private closeOthers(elItem?: HTMLLgAccordionItemElement): void {
    this.elItems.forEach(el => (el !== elItem ? el.toggle(false) : null));
  }
}
